import { CogLayerConfig, PMTilesLayerConfig, SpatialDataset } from "../types";
import { spatialDatasetLayerId } from "../constants/spatialDataset";
import { compileCategoricalMatchLayer, geostylerToMapLibreLayers } from "../utilities/geostylerToMapLibre";
import { geometryTypeFromStyle, spatialDatasetStyleToGeostyler, type SpatialGeometryType } from "../utilities/spatialDatasetStyle";
import { clampMaxZoom } from "../utilities/maplibreZoom";

/**
 * Convert a completed SpatialDataset into the appropriate MapLayerConfig.
 *
 * `display_style` is GeoStyler for vector datasets. Legacy MapLibre paint objects
 * are normalized at read time until the backend data migration is complete.
 */

/**
 * Last-resort geometry guess for a dataset the worker never classified, from a
 * LEGACY flat paint dict (`line-*` / `circle-*` keys). A GeoStyler style has no
 * such keys, so it must be read by {@link geometryTypeFromStyle} first — this
 * function would call every GeoStyler-styled dataset a polygon.
 */
function inferVectorLayerType(displayStyle: Record<string, any>): "fill" | "line" | "circle" {
  const keys = Object.keys(displayStyle ?? {});
  if (keys.some((k) => k.startsWith("line-"))) return "line";
  if (keys.some((k) => k.startsWith("circle-"))) return "circle";
  return "fill";
}

const DEFAULT_PAINT: Record<"fill" | "line" | "circle", Record<string, unknown>> = {
  fill: { "fill-color": "#3b82f6", "fill-opacity": 0.5 },
  line: { "line-color": "#3b82f6", "line-width": 2 },
  circle: { "circle-color": "#3b82f6", "circle-radius": 5 }
};

/**
 * Spatial PMTiles have always been rendered as one direct MapLibre layer against
 * one vector source (unlike CM tables' GeoJSON sources, which render one sibling
 * layer per GeoStyler rule — see `useCmTableMapLayers.ts`). A tiled vector
 * source pays a real per-tile cost for N sibling layers, so a categorical style
 * (several rules on one field) is compiled into a SINGLE data-driven layer via
 * `compileCategoricalMatchLayer` — see the 9 Sep 2026 spatial-dataset styling
 * plan §3.2 (decision D-3). Anything that compiler can't express (a style with
 * just one rule, mixed geometry kinds, a hand-written complex filter someone
 * pasted into the Advanced JSON tab) falls back to today's contract: render
 * whichever fragment matches this geometry first.
 */
function spatialStyleToMapLibreStyle(styleLayers: ReturnType<typeof geostylerToMapLibreLayers>, geometry: SpatialGeometryType) {
  const preferredTypes = geometry === "point" ? ["symbol", "circle"] : geometry === "line" ? ["line"] : ["fill"];
  const candidates = styleLayers.filter((layer) => preferredTypes.includes(layer.type));
  const legacyType = geometry === "point" ? "circle" : geometry === "line" ? "line" : "fill";

  const compiled = compileCategoricalMatchLayer(candidates);
  if (compiled) {
    return {
      type: compiled.type,
      paint: compiled.paint,
      layout: compiled.layout,
      minzoom: compiled.minzoom,
      maxzoom: compiled.maxzoom === undefined ? undefined : clampMaxZoom(compiled.maxzoom)
    };
  }

  const fragment = candidates[0];
  return {
    type: fragment?.type ?? legacyType,
    paint: fragment?.paint ?? DEFAULT_PAINT[legacyType],
    layout: fragment?.layout,
    filter: fragment?.filter,
    minzoom: fragment?.minzoom,
    // The fragment comes from a user-authored GeoStyler document, so it can carry
    // a maxzoom MapLibre will reject. Left undefined when unset so the caller's
    // own fallback still applies.
    maxzoom: fragment?.maxzoom === undefined ? undefined : clampMaxZoom(fragment.maxzoom)
  };
}

/**
 * Reads a raster dataset's Tier-1 paint (opacity/brightness/contrast/saturation/
 * hueRotate/resampling/fadeDuration — see the styling plan §4.1) from its stored
 * style. `raster-opacity` is split out: it's the one raster paint property with
 * an existing live-adjustable UI (`useLayerActions().setLayerOpacity`, which
 * writes `options.opacity` directly), so it's seeded there rather than baked
 * into the static `rasterPaint` object, keeping that control working unchanged.
 */
function rasterStyleToOptionsAndPaint(displayStyle: Record<string, any> | undefined): { opacity: number; paint: Record<string, unknown> } {
  const style = spatialDatasetStyleToGeostyler(displayStyle, "raster");
  const fragment = geostylerToMapLibreLayers(style).find((layer) => layer.type === "raster");
  const paint = { ...(fragment?.paint ?? {}) };
  const opacity = typeof paint["raster-opacity"] === "number" ? (paint["raster-opacity"] as number) : 1;
  delete paint["raster-opacity"];
  return { opacity, paint };
}

export function toLayerConfig(ds: SpatialDataset): CogLayerConfig | PMTilesLayerConfig | null {
  if (!ds.display_url || !ds.display_type) return null;

  const bounds = ds.bbox
    ? ([
        [ds.bbox[0], ds.bbox[1]],
        [ds.bbox[2], ds.bbox[3]]
      ] as [[number, number], [number, number]])
    : undefined;

  if (ds.display_type === "cog") {
    const { opacity, paint } = rasterStyleToOptionsAndPaint(ds.display_style);
    return {
      id: spatialDatasetLayerId(ds.id),
      name: ds.name,
      type: "cog",
      url: ds.display_url,
      initialVisibility: false,
      bounds,
      rasterPaint: paint,
      options: {
        opacity,
        minZoom: 0,
        // maxZoom from backend SDW-13 — treat missing options as no clamp, not a crash
        maxZoom: 24
      }
    } as CogLayerConfig;
  }

  if (ds.display_type === "pmtiles" && ds.dataset_type === "vector") {
    // In order: what the worker classified, then what the stored GeoStyler style
    // symbolizes, then the legacy flat-paint guess. The middle step matters for
    // datasets processed before `geometry_type` existed — without it a styled
    // point dataset resolves to "polygon" and renders as an invisible fill.
    const geometry = (ds.geometry_type ??
      geometryTypeFromStyle(ds.display_style) ??
      ({ circle: "point", line: "line", fill: "polygon" } as const)[inferVectorLayerType(ds.display_style ?? {})]) as SpatialGeometryType;
    const geostylerStyle = spatialDatasetStyleToGeostyler(ds.display_style, geometry, ds.name);
    const style = spatialStyleToMapLibreStyle(geostylerToMapLibreLayers(geostylerStyle), geometry);

    return {
      id: spatialDatasetLayerId(ds.id),
      name: ds.name,
      type: "pmtiles",
      url: ds.display_url,
      layerFormat: "vector",
      // tippecanoe output layer is always the literal string "layer"
      sourceLayer: "layer",
      initialVisibility: false,
      bounds,
      style,
      options: { minZoom: 0, maxZoom: 24 }
    } as PMTilesLayerConfig;
  }

  if (ds.display_type === "pmtiles" && ds.dataset_type === "raster") {
    const { opacity, paint } = rasterStyleToOptionsAndPaint(ds.display_style);
    return {
      id: spatialDatasetLayerId(ds.id),
      name: ds.name,
      type: "pmtiles",
      url: ds.display_url,
      layerFormat: "raster",
      initialVisibility: false,
      bounds,
      rasterPaint: paint,
      options: {
        opacity,
        tileSize: 256,
        minZoom: 0,
        maxZoom: 24
      }
    } as PMTilesLayerConfig;
  }

  return null;
}

/**
 * Layers are registered on demand when the user selects a dataset in SideBar.tsx.
 * This hook is a no-op so existing call sites in MapComponent don't need to change.
 */
export const useSpatialDatasetMapLayers = () => {};
